const en = {
  app: {
    title: "MeW Dispenser Maps",
    signInPrompt: "Sign in with your Google account to prepare the sheet and photo folders.",
    signInGoogle: "Sign in with Google",
    signOut: "Sign out",
    setupBanner: "Almost done. Open the form and link it to your responses spreadsheet: in the form, go to Responses → Link to Sheets → select the existing spreadsheet.",
    openForm: "Open form",
    setupError: "Startup setup failed: {{msg}}",
    dismiss: "Dismiss",
    staleMewTitle: "Previous project not found",
    staleMewBody: "Your browser has stored references to a MeW Maps project that no longer exists in Google Drive. A new project is being created.",
    staleMewImplications: "What this means for your stored data:",
    staleMewImp1: "Clear (recommended) — removes stale folder and file IDs so the new project starts completely fresh.",
    staleMewImp2: "Keep — old Drive references remain in the browser; photos, icons, and assets may be missing or linked to the wrong files.",
    staleMewImp3: "Either way, the new project settings are configured through the setup wizard that follows.",
    staleMewClear: "Clear stale data",
    staleMewKeep: "Keep old references",
  },
  nav: {
    sheet: "Sheet",
    field: "Field entry",
    tasks: "Tasks",
    kml: "KML",
    map: "Map",
    email: "Email",
    members: "Members",
    formSettings: "Form",
    import: "Import",
    fieldworker: "Fieldworker",
    advancedOn: "Advanced mode enabled. Bulk import is now visible.",
    advancedOff: "Advanced mode disabled.",
  },
  account: {
    menu: "Account",
    manager: "Manager",
  },
  onboard: {
    chooseTitle: "Get started",
    chooseSubtitle: "Create your own MeW Maps project, or join one that was shared with you.",
    createTitle: "Create a new project",
    createDesc: "Set up a new sheet and photo folders in your own Google Drive.",
    joinTitle: "Join a shared project",
    joinDesc: "A manager shared their project with you. You'll select the data spreadsheet and the photos folder to do field entry.",
    joining: "Connecting to the shared project…",
    joined: "Joined “{{name}}”. You can now add field entries.",
    ownedTitle: "That folder is your own",
    ownedBody: "“{{name}}” belongs to you, so there is nothing to join. Use “Create a new project” instead.",
    joinError: "Could not join: {{msg}}",
    back: "Back",
  },
  common: {
    processing: "Processing…",
    error: "Error",
    pickerCookieHint: {
      firefox: "Using Firefox? If the file picker doesn't load or shows \"The API developer key is invalid\", Firefox's tracking protection is blocking Google sign-in. Click the shield icon in the address bar and turn off Enhanced Tracking Protection for this site, then try again.",
      safari: "Using Safari? If the file picker doesn't load or shows \"The API developer key is invalid\", Safari's cross-site tracking prevention is blocking Google sign-in. Open Safari → Settings → Privacy and uncheck \"Prevent cross-site tracking\", then reload this page and try again.",
      brave: "Using Brave? If the file picker doesn't load or shows \"The API developer key is invalid\", Brave Shields are blocking Google sign-in. Click the Brave Shields (lion) icon in the address bar and turn Shields down for this site, then try again.",
      generic: "If the file picker opens normally, you can ignore this. If it doesn't load or shows \"The API developer key is invalid\", your browser is likely blocking Google's third-party cookies — allow cookies (or disable tracking protection) for this site and for google.com, then reload and try again.",
    },
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    notSpecified: "(not specified)",
    open: "Open",
    rows: "rows",
    lastSync: "Last sync",
    settings: "Settings",
    language: "Language",
  },
  sheet: {
    commands: "Commands",
    addRow: "Add row",
    importExcel: "Import Excel",
    exportKml: "Export KML",
    generateQr: "Generate QR codes ({{count}})",
    qrSettings: "QR Code Settings",
    qrMessage: "QR caption (optional)",
    qrFormUrl: "Form URL (linked)",
    qrFormUrlMissing: "No form linked yet — open the Form view to create one or import an existing URL.",
    qrFormUrlRequired: "Link a form first (Form view → Create or Import) before generating QR codes.",
    qrManageForm: "Manage form fields",
    qrColumns: "Columns",
    qrWidth: "QR size (px)",
    qrShowLabel: "Show display name",
    qrShowInstallLocation: "Show install location",
    qrRows: "Rows / page (0 = auto)",
    qrPageSize: "Page size",
    qrOrientation: "Orientation",
    qrOrientation_portrait: "Portrait",
    qrOrientation_landscape: "Landscape",
    qrMargin: "Margin (mm)",
    qrGap: "Gap (mm)",
    noRowsSelected: "Please select at least one row",
    qrGenerating: "Generating QR codes…",
    qrGenerated: "Generated QR codes for {{count}} rows",
    qrError: "QR generation error: {{msg}}",
    reload: "Reload sheet",
    openGoogleSheet: "Open Google Sheet",
    addedRow: "Added a new row",
    addError: "Add error: {{msg}}",
    renamePhotosManually: "ID changed {{from}} → {{to}}. Photos stay linked, but their filenames keep the old prefix — rename them in Drive if you want them tidy.",
    importingExcel: "Reading Excel…",
    importNoRows: "No rows found in Excel",
    importedRows: "Imported {{count}} rows",
    importError: "Import error: {{msg}}",
    saveError: "Save error: {{msg}}",
    reportUrlCopied: "Report URL copied: {{id}}",
    copyFailed: "Failed to copy to clipboard",
    enterIdFirst: "Please enter an ID first",
    uploadingPhotos: "Uploading photos for {{id}}…",
    uploadedPhotos: "Uploaded {{count}} photos",
    photoUploadError: "Photo upload error: {{msg}}",
    confirmDelete: "Delete row {{id}} and its photos? This cannot be undone.",
    deletingRow: "Deleting {{id}}…",
    deletedRow: "Deleted {{id}}",
    deleteError: "Delete error: {{msg}}",
    deleteSelected: "Delete selected rows ({{count}})",
    confirmDeleteSelected: "Delete {{count}} selected rows and their photos? This cannot be undone.",
    deletingSelected: "Deleting {{count}} rows…",
    deletedSelected: "Deleted {{count}} rows",
  },
  formSettings: {
    title: "Form Settings",
    intro: "Define the fields of the Google Form used by every QR code. The row labelled \"ID\" is the prefilled entry — the dispenser's ID is injected into it.",
    linkedFormHeader: "Linked Google Form",
    linkedFormSafe: "The linked URL is preserved during Sync as long as the ID question still exists. The app never deletes the form file.",
    notLinkedYet: "No form is linked yet. Click Sync to create one from the schema below, or Import an existing URL.",
    linkPruned: "The previously linked form no longer exists. Cleared the link — use Create from schema to make a new one.",
    linkKept: "Kept the linked form. Access may be granted later.",
    scanFolder: "Scan forms/ folder",
    confirmScanFolder: "Scan the forms/ Drive folder and link the first form found (excluding Template)?",
    scanLinked: "Linked \"{{name}}\" from the forms/ folder.",
    scanError: "Scan error: {{msg}}",
    reuseExistingForm: "Reuse existing form (by ID)…",
    reuseReplace: "Replace with existing form (by ID)…",
    pickReuseTitle: "Select the form to reuse",
    confirmReuseForm: "Link \"{{name}}\" by ID and reuse it as-is? MewMaps will read it to build the QR link but will NEVER edit it (no sync, no publish, no rebuild). Use this when migrating an app whose QR codes already point at this form.",
    reuseLinked: "Reusing \"{{name}}\" (read-only). Its prefilled URL is stored for new QR codes; the form is never modified.",
    reuseLinkedNoId: "Reusing \"{{name}}\" (read-only). It has no \"ID\" question, so the plain form URL was stored — existing QR codes keep working unchanged.",
    reuseError: "Reuse-by-ID error: {{msg}}",
    reusedReadOnly: "Reused by ID — MewMaps reads this form but never edits it.",
    confirmPruneInaccessible: "The linked Google Form isn't accessible right now. Remove the link? Cancel to keep it — access may be granted later.",
    sync: "Sync schema to form",
    syncing: "Syncing to Google Forms…",
    syncedExisting: "Linked Google Form updated. Prefilled URL preserved.",
    syncError: "Form sync error: {{msg}}",
    confirmSyncExisting: "Wipe non-ID items in the linked Google Form and rebuild from the saved schema? The prefilled URL is preserved.",
    createFromSchema: "Create from schema",
    creating: "Creating Google Form…",
    createdFromSchema: "Google Form created from the saved schema. Prefilled URL: {{url}}",
    createError: "Form create error: {{msg}}",
    confirmCreateFromSchema: "Create a new Google Form from the saved schema?",
    importExistingUrl: "Import existing URL",
    replaceUrl: "Replace linked URL…",
    detach: "Detach form",
    confirmDetach: "Detach the linked form? The current URL will be cleared (backed up so you can restore it). The Google Form itself is not deleted.",
    detached: "Form detached. URL cleared and backed up.",
    detachError: "Detach error: {{msg}}",
    createResponsesSheet: "Create responses sheet",
    confirmCreateResponsesSheet: "Create a responses spreadsheet for this form? You then link it to the form yourself: in the form, Responses → Link to Sheets → select this spreadsheet.",
    creatingResponsesSheet: "Creating responses spreadsheet…",
    responsesSheetCreated: "Responses spreadsheet created. Now link it to the form: Responses → Link to Sheets → select it.",
    responsesSheetAlreadyLinked: "A responses spreadsheet is already linked to this form.",
    responsesSheetAdopted: "Using the responses sheet this form is already attached to. Nothing was created or changed.",
    responsesSheetError: "Responses sheet error: {{msg}}",
    useLinkedResponsesSheet: "Use linked responses sheet",
    checkResponsesSheet: "Check responses sheet",
    noSheetAttached: "This reused form has no responses sheet attached. Email notifications need one.",
    createResponsesSheetOptional: "Create responses sheet (optional)",
    confirmCreateDetachedSheet: "This form has no responses sheet attached. Create a new standalone one? You then link it to the form yourself in Google Forms (Responses → Link to Sheets). The form itself is not changed.",
    detachedSheetCreated: "Responses sheet created. Now link it to your form: Responses → Link to Sheets → select it.",
    publishForm: "Publish form",
    confirmPublish: "Publish the linked form and open it to responses?",
    publishing: "Publishing form…",
    published: "Form published and accepting responses.",
    publishError: "Publish error: {{msg}}",
    openResponsesSheet: "Open responses sheet",
    emailBackend: "Email backend setup",
    emailBackendExplain: "Show the code and steps to turn on email notifications from your responses sheet (install the MewMail library and run setup).",
    emailBackendDialogTitle: "Enable email notifications",
    emailBackendDone: "Email backend is already set up.",
    emailBackendNeedForm: "Link a form and create the responses sheet first.",
    restoreBackup: "Restore previous URL",
    colAction: "Action",
    colExplanation: "What it does",
    explain: {
      createFromSchema:
        "Copies the Template form, clears its items, and rebuilds the questions from the saved schema, then stores the new form link. Refuses if a form is already linked.",
      scanFolder:
        "Links the first Google Form found in the Drive forms/ folder (excluding Template) and builds the prefilled URL from its \"ID\" question. Refuses if a form is already linked.",
      reuseExistingForm:
        "Pick any existing Google Form (even one shared from another account) to reuse by ID — ideal when migrating an app whose QR codes already point at it. The form is linked read-only: MewMaps reads it to build the prefilled URL but never edits, publishes, or rebuilds it.",
      importUrl:
        "Sets the linked form to a prefilled URL you paste (must contain &entry.<number>=). The previous link is backed up first. Does not modify the form or responses sheet.",
      sync:
        "Wipes every non-ID item in the linked Google Form and rebuilds them from the saved schema. The prefilled URL is preserved because the \"ID\" question is kept.",
      openGoogleForm: "Opens the linked Google Form's editor in a new browser tab. Link only — changes nothing.",
      createResponsesSheet:
        "Creates (or reuses) a responses spreadsheet in the Drive forms/ folder and records its ID. You then link it to the form yourself (Responses → Link to Sheets), since only you can attach a response destination.",
      reusedResponsesSheet:
        "Adopts the responses sheet this reused form is already attached to — nothing is created or changed. If the form has none attached, offers to create a standalone sheet for you to link in Google Forms.",
      publishForm:
        "Publishes the Google Form and sets it to accept responses (via the Forms API), so its link works for users. Needs only a linked form. Use this if the form was created unpublished, or was later unpublished or closed.",
      openResponsesSheet: "Opens the responses spreadsheet in a new tab. Link only.",
      detach:
        "Only clears the stored form link (formId/URL) into backup slots so you can restore it. Does NOT delete the form, unlink the responses sheet, or clear the saved sheet ID.",
      restoreBackup:
        "Swaps the current form link with the backed-up one — undoes a Detach or Replace. The current and backup values trade places.",
    },
    need: {
      schema: "Add at least one question field to the schema first.",
      notLinked: "Unavailable while a form is already linked.",
      notLocked: "Unavailable: this form is reused by ID and is never edited.",
      linkedForm: "Link a form first.",
      responsesSheet: "Create the responses sheet first.",
      backup: "No backup available yet.",
    },
    titleFirstRequired: "The first row must be of type \"Form title\".",
    noQuestions: "Add at least one question field.",
    alreadyLinked: "A form is already linked. Use Replace to change the URL, or Sync to update fields.",
    importPrompt: "Paste your existing prefilled Google Form URL (must end with &entry.<number>=):",
    replacePrompt: "Replace the linked URL. Current: {{current}}\nPaste the new prefilled URL:",
    confirmReplace: "Replace the linked URL? The previous value will be backed up so you can restore it.",
    urlImported: "URL imported.",
    urlReplaced: "URL replaced. Previous value backed up.",
    importError: "URL import error: {{msg}}",
    confirmRestore: "Swap the linked URL with the backup?",
    restored: "Previous URL restored.",
    restoreError: "Restore error: {{msg}}",
    schemaHeader: "Form schema",
    schemaInfoNote: "Saving the schema only updates the sheet. To apply it to the Google Form on an initial deployment, click \"Create new Google Form\" above. After launch, edit the Google Form directly to avoid changing entry IDs.",
    saveSchema: "Save schema",
    schemaSaved: "Form schema saved to the sheet.",
    schemaSaveError: "Schema save error: {{msg}}",
    confirmSaveSchema: "Save the schema to the sheet? This does not modify the linked Google Form.",
    colLabel: "Label",
    colType: "Type",
    colValues: "Values",
    colDescription: "Description",
    colActions: "Actions",
    valuesPlaceholder: "Option 1|Option 2|Option 3",
    descriptionPlaceholder: "Optional helper text",
    titlePlaceholder: "Form title text",
    formDescriptionPlaceholder: "Form description text",
    sectionTitlePlaceholder: "Section title",
    sectionDescriptionPlaceholder: "Section description (optional)",
    type: {
      text: "Short text",
      textarea: "Paragraph",
      select: "Dropdown",
      radio: "Radio",
      checkbox: "Checkbox",
      title: "Title / Section",
    },
    addField: "Add field",
    moveUp: "Move up",
    moveDown: "Move down",
    dragToReorder: "Drag to reorder",
    remove: "Remove",
    edit: "Edit schema",
    cancel: "Cancel editing",
    locked: "Locked to prevent accidental edits.",
    idFieldMissing: "Add a row labelled \"{{label}}\" so the dispenser ID can be prefilled.",
    choiceValuesMissing: "Field \"{{label}}\" needs at least one value (separate options with |).",
    noFields: "Add at least one field.",
    confirmEdit: "Unlock the schema for editing?",
    openGoogleForm: "Open Google Form",
    templateHeader: "Form template",
    templateHelp: "New forms are copied from MewMaps/forms/Template, so its real header banner, theme, and other settings carry over. Either create a blank template with this button and style it in Google Forms, or pick an existing form (including one shared from another account) with \"Use shared form…\" to adopt its background and theme. Forms made directly in Drive are not visible to the app until created or picked here.",
    editTemplate: "Edit form template",
    templateCreated: "Template form created in MewMaps/forms/Template. Set its header banner in Google Forms, then Create from schema.",
    templateOpened: "Opened the existing template. Edit it in Google Forms, then Create from schema or Sync.",
    templateError: "Template setup failed: {{msg}}",
    importThemedTemplate: "Use shared form…",
    pickTemplateTitle: "Select a form to use as the template",
    confirmThemedTemplate: "Copy \"{{name}}\" into MewMaps/forms/Template and use it as the template for new forms? This replaces the current template.",
    themedTemplateSet: "Copied \"{{name}}\" as the form template. New forms will inherit its background, theme, and header. Questions are still rebuilt from the schema.",
    themedTemplateError: "Could not use the selected form as a template: {{msg}}",
  },
  email: {
    title: "Email Settings",
    dest: "To",
    cc: "Cc",
    bcc: "Bcc",
    senderName: "Sender name",
    subject: "Subject",
    topMessage: "Top message",
    bottomMessage: "Bottom message",
    save: "Save email settings",
    saved: "Email settings saved",
    saveError: "Email settings save error: {{msg}}",
    locked: "These settings are locked to prevent accidental changes.",
    edit: "Edit settings",
    cancel: "Cancel editing",
    confirmSave: "Save changes to the email settings? These affect every report sent.",
    confirmEdit: "Unlock the email settings for editing?",
  },
  members: {
    title: "Members",
    intro: "List teammates who share this project but don't own the Drive folder, and choose what each one sees. Managers get the full app; fieldworkers only get Field entry + Map. This is a team convenience, not a security control — everyone here can still edit the data in Google Sheets directly.",
    introOwner: "Add teammates by email and choose their access. Adding someone shares the project's files with them (as Editor) and emails them an invite; removing someone revokes that access. Managers get the full app; fieldworkers only get Field entry + Map. New members still complete a one-time \"Join a shared project\" in the app after accepting, and managers may be asked to pick a shared folder the first time they open a view that needs it.",
    notOwnerNote: "You're not the project owner. Saving shares the data sheet and emails an invite, but you must also share the MewMaps folder in Google Drive (as Editor) with new members so they get photos, maps and forms.",
    inviteMessage: "You've been added to a MewMaps project. Open MewMaps, sign in with this Google account, and choose \"Join a shared project\" to get started.",
    savedSharePartial: "Saved. Couldn't share with: {{emails}} — check the address(es) and try again.",
    savedShareRootNote: "Saved and shared the data sheet. Some project folders couldn't be shared automatically (one contains a file MewMaps didn't create, e.g. a linked form-responses sheet). Please also open the MewMaps folder in Google Drive and share it (as Editor) with: {{emails}} — that gives them photos, maps and forms.",
    openDriveFolder: "Open the MewMaps folder in Google Drive",
    email: "Email",
    emailPlaceholder: "name@example.com",
    role: "Role",
    roleManager: "Manager (full access)",
    roleFieldworker: "Fieldworker (field + map)",
    empty: "No members listed yet. Shared users default to fieldworker until added here as a manager.",
    addRow: "Add member",
    removeRow: "Remove member",
    signedInAs: "Signed in as {{email}}. As the folder owner you always have full access.",
    save: "Save members",
    saved: "Members saved",
    saveError: "Members save error: {{msg}}",
    locked: "This list is locked to prevent accidental changes.",
    edit: "Edit members",
    cancel: "Cancel editing",
    confirmSave: "Save the members list? This changes what each teammate can see on their next reload.",
    confirmEdit: "Unlock the members list for editing?",
  },
  table: {
    filterCampus: "Filter campus",
    filterBuilding: "Filter building",
    filterOwner: "Filter assignee",
    actions: "Actions",
    reportUrl: "Report URL",
    photos: "Photos",
    folder: "Folder",
    openFolder: "Open Drive folder",
    pictures: "Picture",
    viewPictures: "View pictures",
    openInMaps: "Open in Maps",
    delete: "Delete",
    derivedDisplayName: "Auto-derived from Department + Floor",
    selectRow: "Select this row",
    selectRowNoId: "Row needs an ID to select",
    selectAll: "Select all (filtered)",
  },
  qr: {
    title: "QR codes ({{count}})",
    print: "Print",
    close: "Close",
  },
  pictures: {
    title: "Pictures ({{count}})",
    close: "Close",
    empty: "No pictures found.",
    size: "Thumbnail size",
  },
  headers: {
    "#": "#",
    "ID": "ID",
    "担当者": "Assignee",
    "キャンパス": "Campus",
    "部局、施設名": "Department / Facility",
    "建物": "Building",
    "階": "Floor",
    "設置場所": "Install location",
    "備考": "Notes",
    "アクセス": "Access",
    "表記名①": "Display name",
    "緯度": "Latitude",
    "経度": "Longitude",
    "写真フォルダID": "Photo folder ID",
    "写真ファイルID": "Photo file IDs",
  },
  tasks: {
    title: "Tasks",
    intro: "Records with missing information, grouped by what's needed to complete them. Open one to fix it in the field-entry form.",
    refresh: "Refresh",
    allClear: "Nothing to fix — every record has its ID, location, coordinates and install spot.",
    count: "{{n}} items",
    address: "Address this",
    group: {
      missingId: {
        title: "Missing ID",
        desc: "These records have no ID (often placeholder rows where the building is “未設置”). On site, read the dispenser's QR or confirm against your documentation, then assign the correct ID — it saves back onto the same row.",
      },
      missingLocation: {
        title: "Missing location",
        desc: "Campus, department, building or floor is incomplete (empty or “未設置”). Usually fillable from the facility documentation; a site visit may be needed to confirm the building and floor.",
      },
      missingCoords: {
        title: "Missing coordinates",
        desc: "No map position yet. Go on site and capture GPS with “Get location”, or drop the pin on the map.",
      },
      missingInstallSpot: {
        title: "Missing install location (設置場所)",
        desc: "The exact spot is blank. You can usually infer it from the ID and your documentation, then confirm it on site.",
      },
    },
  },
  field: {
    title: "Field entry",
    id: "ID",
    assignee: "Assignee",
    campus: "Campus",
    department: "Department / Facility",
    building: "Building",
    floor: "Floor",
    installLocation: "Install location",
    access: "Access",
    displayName: "Display name",
    notes: "Notes",
    location: "Get location",
    locating: "Getting location…",
    locationSampling: "Improving accuracy… ±{{m}} m",
    locationGot: "Location captured: {{lat}}, {{lng}} (±{{m}} m)",
    locationFailed: "Could not get location. Please enter manually.",
    locationDenied: "Windows is blocking location. Open Settings → Privacy & security → Location and turn ON: Location services, Let apps access your location, and (at the bottom) Let desktop apps access your location. Then tap “Get location” again — or just place the pin on the map below.",
    accuracy: "Accuracy: ±{{m}} m",
    geoUnavailable: "Geolocation is not supported in this browser",
    latitude: "Latitude",
    longitude: "Longitude",
    mapHint: "Drag the pin or tap the map to set the exact spot",
    openInMaps: "Open in Google Maps",
    mapStandard: "Map",
    mapSatellite: "Satellite",
    photos: "Photos",
    takePhoto: "Take photo",
    choosePhotos: "Choose photos",
    enterId: "Please enter an ID",
    saved: "{{id}} saved",
    updated: "{{id}} updated",
    loadExisting: "Load existing",
    loadedExisting: "Loaded existing values for {{id}}",
    clear: "Clear",
    cleared: "Form cleared",
    idExists: "This ID already exists — submitting will overwrite it.",
    confirmOverwrite: "{{id}} already exists. Overwrite with these values?",
    addWatermark: "Add watermark to photos",
    replaceWatermark: "Replace watermark",
    resetWatermark: "Reset to default",
    confirmResetWatermark: "Replace the current watermark with the bundled default?",
    watermarkUploaded: "Watermark uploaded",
    watermarkReset: "Watermark reset to default",
    watermarkUploadFailed: "Watermark upload failed: {{msg}}",
  },
  kml: {
    title: "KMZ export",
    mapSettings: "Map settings",
    mapName: "Map name",
    filename: "Filename (.kmz auto-appended)",
    commonPicture: "Shared image (shown on every pin)",
    notSet: "Not set",
    chooseImage: "Choose and upload image",
    saveSettings: "Save settings",
    settingsSaved: "Settings saved",
    uploadingShared: "Uploading shared image…",
    uploadedShared: "Shared image uploaded",
    uploadFailed: "Upload failed: {{msg}}",
    filterCampus: "Filter campus",
    filterBuilding: "Filter building",
    countSummary: "Target: {{filtered}} / with GPS {{withGps}} / total {{total}}",
    generate: "Generate & save KMZ",
    generating: "Processing…",
    noGps: "No rows with GPS coordinates",
    generated: "Saved & downloaded KMZ for {{count}} pins",
    openSaved: "Open saved file",
    hint:
      "On mymaps.google.com → create a new map → Import to load the KMZ file. Each pin shows its dispenser photos and the shared image inline (the Drive images are made public on export).",
  },
  importView: {
    title: "Bulk import (XLSX + KML + JSON)",
    intro:
      "Pick the XLSX alone to just append (or overwrite) dispenser rows. Or pick all three files — the XLSX (dispenser metadata with ID), the KML (GPS + photos), and the labeled-places JSON (coordinate → ID) — to also match KML placemarks to the JSON by exact coordinates, recover the dispenser ID, and write GPS + upload photos onto the matching XLSX row. Duplicate photo URLs are skipped.",
    xlsxFile: "XLSX file",
    kmlFile: "KML file",
    jsonFile: "Labeled-places JSON",
    xlsxRowCount: "{{count}} rows",
    placemarkCount: "{{count}} placemarks",
    labeledCount: "{{count}} labeled points",
    preview: "Preview",
    matched: "Matched (KML ↔ JSON ↔ XLSX): {{count}}",
    unmatchedKml: "KML pins with no JSON coord: {{count}}",
    unmatchedJson: "JSON labels with no KML pin: {{count}}",
    willCreate: "{{count}} KML pins have IDs not in the XLSX — new rows will be created.",
    pinsToProcess: "{{count}} KML pins to process (rows on the sheet will be reused; missing IDs get new rows).",
    uniqueUrls: "Unique photo URLs: {{total}} (Drive file IDs {{drive}}, non-Drive {{other}})",
    commonUrls: "{{count}} URLs appear on multiple pins (treated as common — filtered out).",
    placemarksWithoutGps: "{{count}} placemarks have no GPS — they will be skipped.",
    showUnmatchedKml: "Show unmatched KML pins",
    showWillCreate: "Show IDs to be created",
    skipXlsx: "Skip XLSX append (KML + JSON only)",
    overwriteCheckbox: "Overwrite sheet (preserve formatting & extra columns)",
    overwriteHint: "Available because all required columns were detected.",
    overwriteDisabledHint: "Add the remaining required columns to enable sheet overwrite.",
    extrasDetected: "Extra columns detected: {{names}}",
    confirmOverwrite: "This will DELETE the existing 'mew_dispensers' sheet and replace it with the uploaded Excel file. Continue?",
    overwriteColumnOrderError: "Overwrite requires columns A–N in canonical order. Use Append mode instead.",
    confirm: "Confirm import",
    running: "Importing…",
    done: "Import complete.",
    confirmPhrase: "IMPORT",
    confirmPrompt: "This bulk import will modify the sheet. Type {{phrase}} to proceed.",
    confirmMismatch: "Import cancelled — phrase did not match.",
    confirmAppend: "Append rows from the Excel file to the sheet?",
    excelOnlyHint: "Excel-only: rows will be appended (or overwritten if that option is enabled). No GPS/photos are processed without KML + JSON.",
  },
  footer: {
    about: "About",
    privacy: "Privacy Policy",
    terms: "Terms of Service",
  },
  wizard: {
    stepOf: "Step {{current}} of {{total}}",
    stepWelcome: "Welcome",
    stepEmail: "Notifications",
    stepCampus: "Locations",
    stepForm: "Report form",
    welcomeTitle: "Set up your MeW Maps project",
    welcomeBody: "Three quick steps, then MeW Maps creates everything automatically — your spreadsheet and Google Form.",
    welcomeBodyEmail: "Four quick steps, then MeW Maps creates everything automatically — your spreadsheet, Google Form, and email notifications.",
    welcomeCreates: "What gets created automatically:",
    welcomeSheet: "Google Spreadsheet for dispenser records",
    welcomeFolders: "Drive folders for photos, forms, and assets",
    welcomeForm: "A Google Form linked to every QR code",
    welcomeBackend: "A responses spreadsheet for form submissions (you link it to the form)",
    emailTitle: "Email & Notifications",
    emailIntro: "When someone scans a dispenser QR code and submits a report, an email goes to these addresses. Make sure they reach the people responsible for restocking.",
    campusTitle: "Location options",
    campusIntro: "These appear in the Campus dropdown when entering dispenser records. Edit them to match your organization's sites.",
    campusLabel: "Display label",
    campusValue: "Stored value",
    campusAddRow: "Add location",
    campusRemove: "Remove",
    formTitle: "Report form",
    formIntro: "This is the form users see when they scan a QR code. Customize it for your organization — you can always edit it later from the Form view.",
    formIdLocked: "Required — prefilled automatically from the QR code",
    formAddField: "Add field",
    formAddOption: "Add option",
    formRemoveOption: "Remove option",
    formDragToReorder: "Drag to reorder",
    back: "Back",
    next: "Next",
    finish: "Set up project",
    creatingProject: "Creating Google Drive project…",
    savingSettings: "Saving settings…",
    creatingForm: "Creating form and responses sheet…",
    linkingForm: "Linking the existing form…",
    setupError: "Setup error: {{msg}}",
    formModeCreate: "Create a new form",
    formModeCreateHelp: "Build the report form below; MewMaps creates and manages it (copied from your template).",
    formModeReuse: "Reuse an existing form",
    formModeReuseHelp: "Migrating? Point at a form already in use (its QR codes keep working). It is never edited.",
    reuseTitle: "Reuse an existing form",
    reuseIntro: "Select a Google Form you already use — including one shared from another account. MewMaps links it by ID to build the QR link and never edits it.",
    reuseSelectButton: "Select a form…",
    reuseChangeButton: "Choose a different form…",
    reuseReadOnlyNote: "This form is linked read-only. MewMaps reads it to build the prefilled QR URL but never edits, publishes, or rebuilds it — your live form and its existing QR codes are untouched. Its existing responses sheet is reused as-is; no new sheet is created. You still run the (updated) email backend setup on that sheet.",
    reusePickTitle: "Select the form to reuse",
    reuseNeedSelect: "Select the form to reuse, or switch to \"Create a new form\".",
    noSheetAttached: "The reused form has no responses sheet attached yet. Email notifications need one — create it now (optional), then link it in Google Forms (Responses → Link to Sheets). You can also skip and do it later.",
    createSheetOptional: "Create responses sheet",
    creatingSheet: "Creating…",
    templateSectionTitle: "Theme & background (optional)",
    templateHelp: "Pick an existing form whose background and theme you want the new form to copy. Your questions still come from the schema below — only the look is reused. Leave empty for a plain form (you can set a theme later in Google Forms).",
    templateUseButton: "Use a themed template…",
    templateChangeButton: "Choose a different template…",
    templateClear: "Remove template",
    templatePickTitle: "Select a form to use as the template",
    applyingTemplate: "Applying the template…",
    doneTitle: "Project ready 🎉",
    doneIntro: "Your spreadsheet, Google Form, and responses sheet are set up. To send an email notification whenever someone submits the form, complete the one-time backend setup below. You can also skip it and do it later from the Form view.",
    doneFinish: "Finish",
  },
  emailBackend: {
    title: "Enable email notifications (optional)",
    intro: "Notification emails are sent by a small Apps Script that lives in your responses sheet. Set it up once with the steps below — MewMaps never sends email itself.",
    step1: "In your responses sheet, open Extensions ▸ Apps Script.",
    openResponsesSheet: "Open responses sheet",
    step2: "Add the MewMail library: Libraries ▸ + ▸ paste the Script ID below, set the identifier to {{identifier}}, then Add.",
    shareNote: "The MewMail library is not on the marketplace. Ask the project developer to share read access with you before adding it.",
    step3: "Paste this code into Code.gs, replacing anything already there:",
    step4: "Select the setup function and Run it once, granting the permissions it requests. This also links the form to your responses sheet.",
    scriptIdLabel: "Library Script ID",
    codeLabel: "Code.gs",
    copy: "Copy",
    copied: "Copied",
  },
} as const;

export default en;
